import { useEffect, useMemo, useRef, useState } from 'react';
import { RELATION_TYPES, relationName } from '@/lib/relations';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, Users, X } from 'lucide-react';
import {
  AddButton, Field, LiveScreen, QueryState, Select, formatDate,
  StatusPill, formatNumber, useDebounced, useFormErrors, type Column,
} from '@/components/live';
import { RecordDrawer } from '@/components/RecordDrawer';
import { RankedList } from '@/components/RankedList';
import { FarmerKycSection } from '@/screens/parts/FarmerKycSection';
import { KycRecordDrawer, saveKycRecord, type PendingKyc } from '@/screens/parts/KycRecordDrawer';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { statusStats } from '@/lib/recordStatus';
import { useRows, useSaveRow } from '@/hooks/useResource';
import { callRpc, createRow, saveRow } from '@/api/resources';
import { uploadDocument } from '@/api/documents';
import { DropZone } from '@/components/DropZone';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import { HouseLocationPicker, type HousePoint } from '@/screens/parts/HouseLocationPicker';
import { farmerOptions, placeLabelOf, stateNameOf } from '@/api/lookups';
import { languageName, languageOption, languagesForState, stateLanguage, withRequiredLanguages } from '@/lib/languages';
import { useQuery } from '@tanstack/react-query';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useAuth } from '@/auth/AuthProvider';
import { can, normaliseIdentifier, scopeOf } from '@/auth/session';
import { toast } from '@/lib/toast';
import type { FarmerRow, PartyRow } from '@/types/records';
import { GeographyPicker } from '@/screens/parts/GeographyPicker';
import { FarmerDetail } from '@/screens/parts/FarmerDetail';
import { CastePicker } from '@/screens/parts/CastePicker';
import { namesDiffer } from '@/lib/nameMatch';
import { NameMatchBasisSelect } from '@/components/NameMatchBasisSelect';


const GENDERS = [
  { id: '', label: '--' }, { id: 'female', label: 'Female' }, { id: 'male', label: 'Male' },
  { id: 'other', label: 'Other' }, { id: 'not_disclosed', label: 'Prefer not to say' },
];

/** The channels a farmer can rank, in the order a new farmer's list offers them. */
const CHANNEL_IDS = ['whatsapp', 'sms', 'call', 'email'] as const;
type ChannelId = typeof CHANNEL_IDS[number];
const CHANNEL_LABEL: Record<ChannelId, string> = { whatsapp: 'WhatsApp', sms: 'SMS', call: 'Phone call', email: 'Email' };
/** The contact each channel goes to. A call rings the mobile, so SMS and call share it. */
const CHANNEL_FIELD: Record<ChannelId, 'mobile' | 'whatsapp_number' | 'email'> = {
  whatsapp: 'whatsapp_number', sms: 'mobile', call: 'mobile', email: 'email',
};
const CHANNEL_NEEDS: Record<ChannelId, string> = {
  whatsapp: 'a WhatsApp number - even if it is the same as the mobile', sms: 'a mobile number',
  call: 'a mobile number', email: 'an email address',
};
/** What the footer names when Register is held back. */
const NEEDED_LABEL: Record<string, string> = {
  full_name: 'name', entity_type: 'entity type', village_id: 'village', mobile: 'mobile',
  whatsapp_number: 'WhatsApp number', email: 'email', pin_code: 'PIN code', language_order: 'language',
  channel_order: 'a contact channel', referrer_pick: 'referrer', referred_by_name: "referrer's name",
  referred_by_phone: "referrer's phone",
};

type FormTab = 'basics' | 'kyc' | 'address' | 'contact' | 'reference';
const FORM_TABS: Array<{ id: FormTab; label: string }> = [
  { id: 'basics', label: 'Member' },
  // After Member (owner, 2026-09-27: "farmer KYC can be added to farmer register as a tab? after
  // member tab? -- rearrange the fields accordingly"): the name as per ID card and the KYC documents.
  { id: 'kyc', label: 'KYC' },
  { id: 'address', label: 'Address & house' },
  { id: 'contact', label: 'Contact' },
  { id: 'reference', label: 'Referred by' },
];
/** Which tab each validated field lives on, so an error can take the officer to it. */
const TAB_OF: Record<string, FormTab> = {
  full_name: 'basics', father_or_spouse_name: 'basics', relation_type: 'basics', entity_type: 'basics', name_match_basis: 'kyc', name_match_remarks: 'kyc',
  referred_by_phone: 'reference', referrer_pick: 'reference', referred_by_name: 'reference',
  village_id: 'address', communication_address: 'address', pin_code: 'address',
  mobile: 'contact', whatsapp_number: 'contact', email: 'contact', channel_order: 'contact', language_order: 'contact',
};

interface FormState {
  full_name: string;
  name_as_per_id: string;
  name_match_basis: string;
  name_match_remarks: string;
  father_or_spouse_name: string;
  relation_type: string;
  village_id: string;
  mobile: string;
  whatsapp_number: string;
  email: string;
  /** Offline: reached in person only, so nothing is sent (owner, 2026-09-27: "Offline, Digital?"). */
  contact_mode: 'digital' | 'offline';
  /** Ranked, first choice first; stored empty when offline (migration 0158). */
  channel_order: string[];
  language_order: string[];
  communication_address: string;
  pin_code: string;
  gender: string;
  member_type: string;
  entity_type: string;
  registration_no: string;
  contact_person_name: string;
  social_category: string;
  caste: string;
  is_indigenous: '' | 'yes' | 'no';
  indigenous_community: string;
  referred_by_type: string;
  referred_by_farmer_id: string;
  /** Referred by a farmer who is not on the register yet: name and phone instead of a pick. */
  referrer_unlisted: boolean;
  /** '' not referred, NEW_REFERRER, or the `referrer_key` of a reference already on record. */
  referrer_pick: string;
  /** A photo already stored for the referrer, reused when an existing reference is picked. */
  referred_by_photo_document_id: string;
  referred_by_name: string;
  referred_by_phone: string;
}

const EMPTY: FormState = {
  full_name: '', name_as_per_id: '', name_match_basis: '', name_match_remarks: '', father_or_spouse_name: '', relation_type: '', village_id: '', mobile: '',
  whatsapp_number: '', email: '', contact_mode: 'digital', channel_order: [], language_order: ['en'],
  communication_address: '', pin_code: '', gender: '',
  member_type: 'farmer', entity_type: '', registration_no: '', contact_person_name: '',
  social_category: '', caste: '', is_indigenous: '', indigenous_community: '',
  referred_by_type: '', referred_by_farmer_id: '', referrer_unlisted: false, referrer_pick: '', referred_by_photo_document_id: '', referred_by_name: '', referred_by_phone: '',
};

const ENTITY_TYPES = [
  { id: 'fpo', label: 'Farmer Producer Organisation (FPO)' }, { id: 'cooperative', label: 'Cooperative' },
  { id: 'shg', label: 'Self-help group (SHG)' }, { id: 'society', label: 'Society' },
  { id: 'trust', label: 'Trust' }, { id: 'company', label: 'Company' }, { id: 'other', label: 'Other' },
];
/** The party's own category follows the entity type. */
const PARTY_CATEGORY_OF: Record<string, string> = {
  fpo: 'cooperative', cooperative: 'cooperative', shg: 'cooperative', society: 'cooperative',
  company: 'company', trust: 'other', other: 'other',
};
const COMMUNITIES = [
  { id: '', label: '--' }, { id: 'others', label: 'Others' }, { id: 'obc', label: 'OBC' },
  { id: 'sc', label: 'SC' }, { id: 'st', label: 'ST' }, { id: 'ews', label: 'EWS' },
  { id: 'not_disclosed', label: 'Prefer not to say' },
];
/** The reference picker's "add a new referrer" entry; never stored. */
const NEW_REFERRER = '__new';
/** One referrer already on record, as `api.farmer_references` lists them. */
interface ReferenceOption {
  referrer_key: string;
  referred_by_type: string;
  referred_by_name: string | null;
  referred_by_phone: string | null;
  referred_by_farmer_id: string | null;
  farmers_referred: number;
  referrer_photo_document_id: string | null;
}
/** The same key `api.farmer_references` groups by, so a saved farmer finds its own referrer. */
function referrerKeyOf(row: { referred_by_farmer_id?: string | null; referred_by_name?: string | null; referred_by_phone?: string | null }): string {
  return row.referred_by_farmer_id ?? `${(row.referred_by_name ?? '').trim().toLowerCase()}|${row.referred_by_phone ?? ''}`;
}
/** Every referrer field cleared, for a change of kind or of person. */
const NO_REFERRER = {
  referred_by_type: '', referrer_pick: '', referred_by_farmer_id: '', referrer_unlisted: false,
  referred_by_name: '', referred_by_phone: '', referred_by_photo_document_id: '',
};
function isFarmerKind(type: string): boolean {
  return type === 'farmer' || type === 'lead_farmer';
}
const REFERRER_LABEL: Record<string, string> = {
  farmer: 'Farmer', lead_farmer: 'Lead farmer', field_staff: 'Field staff', stakeholder: 'Stakeholder',
  community_leader: 'Community leader', other: 'Other',
};
const REFERRERS = [
  { id: '', label: 'Not referred' }, { id: 'farmer', label: 'Another farmer' }, { id: 'lead_farmer', label: 'Lead farmer' },
  { id: 'field_staff', label: 'Field staff' }, { id: 'stakeholder', label: 'Stakeholder' },
  { id: 'community_leader', label: 'Community leader' }, { id: 'other', label: 'Other' },
];

/**
 * Farmer onboarding.
 *
 * A farmer is two records: the PARTY, which owns identity, the three contact channels and the
 * KYC evidence, and the FARMER, which is that party in the role of a grower in a village. The
 * form writes both, because asking a field officer to create a party first would be an
 * accurate description of the schema and a poor description of the job.
 */
export default function FarmerRegisterScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  // The header search lands here as ?q=<code>, so the register opens filtered to that record.
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  useEffect(() => { const q = params.get('q'); if (q !== null) setSearch(q); }, [params]);
  const debounced = useDebounced(search);
  const [editing, setEditing] = useState<FarmerRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<FarmerRow | null>(null);
  const [stat, setStat] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  // The name on the ID card, if written differently, needs its basis and remarks (0076).
  const idNameDiffers = namesDiffer(form.name_as_per_id, form.full_name);
  const [saving, setSaving] = useState(false);
  // Grouped into tabs (owner, 2026-09-21): who, where, how to reach them.
  const [formTab, setFormTab] = useState<FormTab>('basics');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  // KYC records added before the farmer exists, saved once it does.
  const [pendingKyc, setPendingKyc] = useState<PendingKyc[]>([]);
  // The referrer's photo, for one typed in by name and phone (migration 0159).
  const [referrerPhotoFile, setReferrerPhotoFile] = useState<File | null>(null);
  const [referrerPhotoPreview, setReferrerPhotoPreview] = useState<string | null>(null);
  const [house, setHouse] = useState<HousePoint | null>(null);
  const [houseChanged, setHouseChanged] = useState(false);
  const { errors, check, clear } = useFormErrors();

  const farmers = useRows<FarmerRow>('farmers', {
    search: debounced ? { columns: ['full_name', 'farmer_code', 'mobile'], term: debounced } : undefined,
    orderBy: { column: 'created_at', ascending: false },
  });

  // The party is read only when a farmer is open for editing: its contact channels live there.
  const party = useRows<PartyRow>('parties', { filters: { id: editing?.party_id } }, Boolean(editing?.party_id));
  const saveFarmer = useSaveRow<FarmerRow>('farmers');
  const existingPhoto = usePhotoUrls([editing?.photo_document_id, editing?.referred_by_photo_document_id]);
  // Languages follow the village's state: English and Hindi everywhere, plus the state's own.
  const villageState = useQuery({
    queryKey: ['state-of', form.village_id],
    queryFn: () => stateNameOf(form.village_id),
    enabled: isSupabaseConfigured && Boolean(form.village_id),
  });
  const referrers = useQuery({
    queryKey: ['picker', 'farmers'],
    queryFn: () => farmerOptions(),
    enabled: isSupabaseConfigured && (creating || Boolean(editing)) && (form.referred_by_type === 'farmer' || form.referred_by_type === 'lead_farmer'),
  });
  // References already on record, offered first so the same referrer is not typed in twice.
  const references = useRows<ReferenceOption & { id?: string }>('farmer_references',
    { orderBy: { column: 'farmers_referred', ascending: false } }, creating || Boolean(editing));
  const referencePhotos = usePhotoUrls((references.data ?? []).map((row) => row.referrer_photo_document_id));
  const villagePlace = useQuery({
    queryKey: ['place-of', form.village_id],
    queryFn: () => placeLabelOf(form.village_id),
    enabled: isSupabaseConfigured && Boolean(form.village_id),
  });
  const languageOptions = useMemo(() => {
    const options = languagesForState(villageState.data);
    // Never hide a language already recorded for this farmer.
    const recorded = form.language_order
      .filter((code) => !options.some((own) => own.id === code))
      .map(languageOption);
    return [...options, ...recorded];
  }, [villageState.data, form.language_order]);
  // English is always first and the state's language second, switched on as soon as the village is
  // known (owner, 2026-09-28): every consent is made in both. A third language stays the farmer's choice.
  const requiredLanguage = stateLanguage(villageState.data);
  useEffect(() => {
    if (!(creating || editing)) return;
    setForm((current) => {
      const next = withRequiredLanguages(current.language_order, villageState.data);
      return next.join() === current.language_order.join() ? current : { ...current, language_order: next };
    });
  }, [villageState.data, creating, editing]);
  const pinnedLanguages = useMemo(() => ({
    en: 'English is on every consent and notice.',
    ...(requiredLanguage ? { [requiredLanguage]: `${languageName(requiredLanguage)} is the state's language, required beside English.` } : {}),
  }), [requiredLanguage]);

  const mayCreate = can(profile, 'farmer.register', 'create');
  const mayEdit = can(profile, 'farmer.register', 'update');
  const scope = scopeOf(profile, 'farmer.register');

  /**
   * What a register is actually asked at a glance: how many people, and how many are stuck.
   * Then the four record statuses, and the two gaps that stop a farmer counting towards
   * anything: KYC not verified, and no mapped plot.
   */
  const STATS = useMemo(() => [
    { key: 'all', label: 'Farmers', match: () => true, tone: 'neutral' as const },
    { key: 'draft', label: 'Drafts', tone: 'info' as const, match: (row: FarmerRow) => Boolean(row.is_draft) },
    ...statusStats<FarmerRow>((row) => row.status),
    { key: 'women', label: 'Women', tone: 'info' as const, match: (row: FarmerRow) => row.gender === 'female' },
    { key: 'entity', label: 'Entities', tone: 'info' as const, match: (row: FarmerRow) => row.member_type === 'entity' },
    { key: 'kyc', label: 'KYC outstanding', tone: 'warn' as const,
      match: (row: FarmerRow) => row.kyc_status !== 'verified' },
    { key: 'unmapped', label: 'No mapped plot', tone: 'error' as const,
      match: (row: FarmerRow) => (row.mapped_plot_count ?? 0) === 0 },
  ], []);

  const visible = useMemo(
    () => applyStat(farmers.data ?? [], STATS, stat),
    [farmers.data, STATS, stat],
  );

  const columns: Array<Column<FarmerRow>> = useMemo(() => [
    { key: 'farmer_code', label: 'Farmer code' },
    { key: 'full_name', label: 'Farmer', render: (row) => (
      <div>
        <strong>{row.full_name}</strong>{row.member_type === 'entity' && <span className="draft-badge entity-badge">Entity</span>}{row.is_draft && <span className="draft-badge">Draft</span>}
        {row.father_or_spouse_name && <div className="muted">{relationName(row.relation_type, row.father_or_spouse_name)}</div>}
      </div>
    ) },
    { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
    { key: 'mobile', label: 'Mobile', render: (row) => row.mobile ?? '--' },
    { key: 'kyc_status', label: 'KYC', render: (row) => <StatusPill value={row.kyc_status} /> },
    { key: 'consent_status', label: 'Consent', render: (row) => <StatusPill value={row.consent_status} /> },
    { key: 'plot_count', label: 'Plots', align: 'right', render: (row) =>
      `${formatNumber(row.plot_count)}${row.mapped_plot_count ? ` (${row.mapped_plot_count} mapped)` : ''}` },
    { key: 'total_mapped_area_ha', label: 'Mapped area', align: 'right', render: (row) => formatNumber(row.total_mapped_area_ha, 2) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
  ], []);

  function resetExtras(row?: FarmerRow) {
    setFormTab('basics');
    setPendingKyc([]);
    setPhotoFile(null);
    setPhotoPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
    setReferrerPhotoFile(null);
    setReferrerPhotoPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
    setHouse(row && row.house_lat != null && row.house_lng != null
      ? { lat: Number(row.house_lat), lng: Number(row.house_lng), accuracy: row.house_accuracy_meters ?? null }
      : null);
    setHouseChanged(false);
  }

  function openCreate() {
    clear();
    setForm(EMPTY);
    resetExtras();
    setCreating(true);
  }

  function pickPhoto(file: File | undefined, forReferrer = false) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Choose a photograph (JPEG or PNG).'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('That photograph is larger than 8 MB.'); return; }
    (forReferrer ? setReferrerPhotoFile : setPhotoFile)(file);
    (forReferrer ? setReferrerPhotoPreview : setPhotoPreview)((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(file); });
  }

  /*
   * The party's contact channels arrive AFTER the drawer opens: the `party` query above only
   * starts once `editing` is set, so nothing read here at open time is there yet. This used to
   * read `party.data` anyway - blank on a first edit - and the save then wrote null over the
   * stored WhatsApp number and email (field audit, 2026-09-27). The effect below fills the three
   * channels once the party for THIS farmer has loaded, and only once, so it never overwrites what
   * the person has since typed.
   */
  const contactLoadedFor = useRef<string | null>(null);
  useEffect(() => {
    const contact = party.data?.[0];
    if (!editing?.party_id || !contact || contact.id !== editing.party_id) return;
    if (contactLoadedFor.current === editing.party_id) return;
    contactLoadedFor.current = editing.party_id;
    setForm((now) => ({
      ...now,
      mobile: contact.primary_mobile ?? now.mobile,
      whatsapp_number: contact.whatsapp_number ?? '',
      email: contact.primary_email ?? '',
    }));
  }, [party.data, editing?.party_id]);

  function openEdit(row: FarmerRow) {
    clear();
    contactLoadedFor.current = null;
    setForm({
      full_name: row.full_name,
      name_as_per_id: row.name_as_per_id ?? '',
      name_match_basis: row.name_match_basis ?? '',
      name_match_remarks: row.name_match_remarks ?? '',
      father_or_spouse_name: row.father_or_spouse_name ?? '',
      relation_type: row.relation_type ?? '',
      village_id: row.village_id,
      mobile: row.mobile ?? '',
      whatsapp_number: '',
      email: '',
      ...(() => {
        const order = row.channel_order ?? (row.communication_preference === 'none' ? [] : [row.communication_preference]);
        return { contact_mode: order.length === 0 ? 'offline' as const : 'digital' as const, channel_order: order };
      })(),
      language_order: row.language_order ?? [row.preferred_language],
      communication_address: row.communication_address ?? '',
      pin_code: row.pin_code ?? '',
      gender: row.gender ?? '',
      member_type: row.member_type ?? 'farmer',
      entity_type: row.entity_type ?? '',
      registration_no: row.registration_no ?? '',
      contact_person_name: row.contact_person_name ?? '',
      social_category: row.social_category ?? '',
      caste: row.caste ?? '',
      is_indigenous: row.is_indigenous == null ? '' : row.is_indigenous ? 'yes' : 'no',
      indigenous_community: row.indigenous_community ?? '',
      referred_by_type: row.referred_by_type ?? '',
      referred_by_farmer_id: row.referred_by_farmer_id ?? '',
      referrer_unlisted: (row.referred_by_type === 'farmer' || row.referred_by_type === 'lead_farmer')
        && !row.referred_by_farmer_id && Boolean(row.referred_by_name),
      referrer_pick: row.referred_by_type ? referrerKeyOf(row) : '',
      referred_by_photo_document_id: row.referred_by_photo_document_id ?? '',
      referred_by_name: row.referred_by_farmer_id ? '' : row.referred_by_name ?? '',
      referred_by_phone: row.referred_by_phone ?? '',
    });
    resetExtras(row);
    setEditing(row);
  }

  /**
   * Contact validation is shared with sign-in (`normaliseIdentifier`), so a number that is
   * good enough to store is good enough to receive a one-time code on. A farmer whose
   * WhatsApp number is stored loosely is a farmer whose consent cannot be collected.
   */
  /** A kind of referrer needs the person; a new one needs at least their name. */
  function referrerProblems(): Record<string, string | undefined> {
    if (!form.referred_by_type) return {};
    if (!form.referrer_pick) return { referrer_pick: 'Choose the referrer, or add a new one.' };
    if (form.referrer_pick === NEW_REFERRER && !form.referred_by_name.trim()) return { referred_by_name: 'Enter the referrer\'s name.' };
    return {};
  }

  function clearReferrerPhoto() {
    setReferrerPhotoFile(null);
    setReferrerPhotoPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
  }

  function validate(asDraft = false): Record<string, string | undefined> {
    // A draft needs only what the database needs to create the row: a name, and the village the
    // farmer code (WB_ML_1) is issued from. Whatever else IS filled in must still be valid.
    const problems: Record<string, string | undefined> = {
      full_name: form.full_name.trim() ? undefined : (form.member_type === 'entity' ? 'The entity\'s name is required.' : 'A name is required.'),
      village_id: form.village_id ? undefined : 'Choose the village.',
    };
    for (const [key, channel] of [['mobile', 'sms'], ['whatsapp_number', 'whatsapp'], ['email', 'email']] as const) {
      const value = form[key].trim();
      if (!value) continue;
      const result = normaliseIdentifier(channel, value);
      if ('error' in result) problems[key] = result.error;
    }
    if (form.pin_code.trim() && !/^[1-9][0-9]{5}$/.test(form.pin_code.trim())) {
      problems.pin_code = 'A PIN code is six digits and does not start with 0.';
    }
    if (form.language_order[0] !== 'en') problems.language_order = 'English comes first.';
    else if (requiredLanguage && form.language_order[1] !== requiredLanguage) problems.language_order = `${languageName(requiredLanguage)} comes second, beside English.`;
    if (asDraft) return problems;
    if (form.contact_mode === 'offline') return { ...problems, ...referrerProblems() };
    if (form.channel_order.length === 0) problems.channel_order = 'Switch on at least one channel, or choose Offline.';
    // A channel that is switched on must have somewhere to go. WhatsApp codes are sent to
    // whatsapp_number alone (comms.fn_channel_destination): the mobile is no fallback.
    for (const channel of form.channel_order.filter((id): id is ChannelId => (CHANNEL_IDS as readonly string[]).includes(id))) {
      const key = CHANNEL_FIELD[channel];
      if (!problems[key] && !form[key].trim()) problems[key] = `${CHANNEL_LABEL[channel]} is switched on, so it needs ${CHANNEL_NEEDS[channel]}.`;
    }
    return { ...problems, ...referrerProblems() };
  }

  function normalised(key: 'mobile' | 'whatsapp_number' | 'email'): string | null {
    const raw = form[key].trim();
    if (!raw) return null;
    const channel = key === 'email' ? 'email' : key === 'mobile' ? 'sms' : 'whatsapp';
    const result = normaliseIdentifier(channel, raw);
    return 'value' in result ? result.value : raw;
  }

  /**
   * What still stops a full save. Register stays disabled until this is empty, and the footer
   * names what is missing (owner, 2026-09-27: "disable register farmer until all fields are
   * filled"). Save draft is not held back - a draft exists to be finished later.
   */
  const outstanding = (() => {
    const problems = validate(false);
    if (form.member_type === 'entity' && !form.entity_type) problems.entity_type = 'What kind of entity is it?';
    return Object.keys(problems).filter((key) => problems[key]);
  })();

  async function handleSave(asDraft = false) {
    const isEntity = form.member_type === 'entity';
    // A farmer picked from the register is stored by id; anyone else, including a farmer who is
    // not on the register yet, by name and phone.
    const referrerIsFarmer = (form.referred_by_type === 'farmer' || form.referred_by_type === 'lead_farmer') && !form.referrer_unlisted;
    let referrerPhone: string | null = null;
    if (!referrerIsFarmer && form.referred_by_phone.trim()) {
      const result = normaliseIdentifier('sms', form.referred_by_phone.trim());
      if ('error' in result) { check({ referred_by_phone: result.error }); setFormTab('reference'); return; }
      referrerPhone = result.value;
    }
    const problems = validate(asDraft);
    if (isEntity && !form.entity_type) problems.entity_type = 'What kind of entity is it?';
    if (!check(problems)) {
      // Jump to the tab holding the first problem, so the error is not hidden behind another tab.
      const first = Object.keys(problems).find((key) => problems[key]);
      if (first) setFormTab(TAB_OF[first] ?? 'basics');
      return;
    }
    setSaving(true);
    try {
      let farmerId: string;
      if (editing) {
        farmerId = editing.id;
        await saveFarmer.mutateAsync({
          id: editing.id,
          values: {
            full_name: form.full_name.trim(),
            name_as_per_id: form.name_as_per_id.trim() || null,
            name_match_basis: idNameDiffers ? form.name_match_basis || null : null,
            name_match_remarks: idNameDiffers ? form.name_match_remarks.trim() || null : null,
            father_or_spouse_name: form.father_or_spouse_name.trim() || null,
            relation_type: form.father_or_spouse_name.trim() ? form.relation_type || null : null,
            gender: isEntity ? null : form.gender || null,
            member_type: form.member_type,
            entity_type: isEntity ? form.entity_type || null : null,
            registration_no: isEntity ? form.registration_no.trim() || null : null,
            contact_person_name: isEntity ? form.contact_person_name.trim() || null : null,
            social_category: isEntity ? null : form.social_category || null,
            caste: isEntity ? null : form.caste.trim() || null,
            is_indigenous: isEntity || form.is_indigenous === '' ? null : form.is_indigenous === 'yes',
            indigenous_community: !isEntity && form.is_indigenous === 'yes' ? form.indigenous_community.trim() || null : null,
            referred_by_type: form.referred_by_type || null,
            referred_by_farmer_id: referrerIsFarmer ? form.referred_by_farmer_id || null : null,
            referred_by_name: referrerIsFarmer ? null : form.referred_by_name.trim() || null,
            referred_by_phone: referrerIsFarmer ? null : referrerPhone,
            village_id: form.village_id,
            mobile: normalised('mobile'),
            channel_order: form.contact_mode === 'offline' ? [] : form.channel_order,
            language_order: form.language_order,
            communication_address: form.communication_address.trim() || null,
            pin_code: form.pin_code.trim() || null,
            is_draft: asDraft,
          },
        });
        await saveRow<PartyRow>('parties', editing.party_id, {
          display_name: form.full_name.trim(),
          primary_mobile: normalised('mobile'),
          whatsapp_number: normalised('whatsapp_number'),
          primary_email: normalised('email'),
          primary_geography_id: form.village_id,
        });
        await queryClient.invalidateQueries({ queryKey: ['parties'] });
      } else {
        // Party first: the farmer row points at it, and a farmer with no party has no identity,
        // no KYC and no consent. If the second write fails the party is left behind as a draft
        // rather than silently orphaned - it is visible on the party register and can be reused.
        // Neither code is supplied: platform.fn_issue_record_code stamps PTY- and FRM- on
        // insert, and a client-chosen code would collide the moment two devices are offline in
        // the same village.
        const created = await createRow<PartyRow>('parties', {
          display_name: form.full_name.trim(),
          party_category: isEntity ? PARTY_CATEGORY_OF[form.entity_type] ?? 'other' : 'individual',
          primary_mobile: normalised('mobile'),
          whatsapp_number: normalised('whatsapp_number'),
          primary_email: normalised('email'),
          primary_geography_id: form.village_id,
          status: 'pending',
        });
        const createdFarmer = await saveFarmer.mutateAsync({
          values: {
            party_id: created.id,
            full_name: form.full_name.trim(),
            name_as_per_id: form.name_as_per_id.trim() || null,
            name_match_basis: idNameDiffers ? form.name_match_basis || null : null,
            name_match_remarks: idNameDiffers ? form.name_match_remarks.trim() || null : null,
            father_or_spouse_name: form.father_or_spouse_name.trim() || null,
            relation_type: form.father_or_spouse_name.trim() ? form.relation_type || null : null,
            gender: isEntity ? null : form.gender || null,
            member_type: form.member_type,
            entity_type: isEntity ? form.entity_type || null : null,
            registration_no: isEntity ? form.registration_no.trim() || null : null,
            contact_person_name: isEntity ? form.contact_person_name.trim() || null : null,
            social_category: isEntity ? null : form.social_category || null,
            caste: isEntity ? null : form.caste.trim() || null,
            is_indigenous: isEntity || form.is_indigenous === '' ? null : form.is_indigenous === 'yes',
            indigenous_community: !isEntity && form.is_indigenous === 'yes' ? form.indigenous_community.trim() || null : null,
            referred_by_type: form.referred_by_type || null,
            referred_by_farmer_id: referrerIsFarmer ? form.referred_by_farmer_id || null : null,
            referred_by_name: referrerIsFarmer ? null : form.referred_by_name.trim() || null,
            referred_by_phone: referrerIsFarmer ? null : referrerPhone,
            village_id: form.village_id,
            mobile: normalised('mobile'),
            channel_order: form.contact_mode === 'offline' ? [] : form.channel_order,
            language_order: form.language_order,
            communication_address: form.communication_address.trim() || null,
            pin_code: form.pin_code.trim() || null,
            is_draft: asDraft,
          },
        });
        farmerId = createdFarmer.id;
        for (const item of pendingKyc) {
          try {
            await saveKycRecord(created.id, profile?.organization?.id ?? null, item);
          } catch (error) {
            toast.error(`Farmer saved, but the ${item.typeLabel} was not: ${error instanceof Error ? error.message : 'save failed'}. Add it on the KYC tab.`);
          }
        }
        if (pendingKyc.length > 0) await queryClient.invalidateQueries({ queryKey: ['party_kyc_records'] });
        await queryClient.invalidateQueries({ queryKey: ['parties'] });
      }

      // The photo and the house point attach to the saved farmer. If either fails the farmer is
      // still saved, and the officer is told what to redo rather than losing the whole form.
      if (photoFile && profile?.organization) {
        try {
          const document = await uploadDocument({
            file: photoFile, bucket: 'farmer-photos', organizationId: profile.organization.id, ownerId: farmerId,
          });
          await callRpc('fn_set_farmer_photo', { p_farmer_id: farmerId, p_document_id: document.id });
        } catch (error) {
          toast.error(`Farmer saved, but the photo was not: ${error instanceof Error ? error.message : 'upload failed'}.`);
        }
      }
      // The referrer's photo belongs only to a referrer typed in by name; picking one from the
      // register, or nobody, clears a photo left from before.
      const referrerByName = !referrerIsFarmer && Boolean(form.referred_by_type);
      if (referrerByName && referrerPhotoFile && profile?.organization) {
        try {
          const document = await uploadDocument({
            file: referrerPhotoFile, bucket: 'farmer-photos', organizationId: profile.organization.id, ownerId: farmerId,
          });
          await callRpc('fn_set_referrer_photo', { p_farmer_id: farmerId, p_document_id: document.id });
        } catch (error) {
          toast.error(`Farmer saved, but the referrer's photo was not: ${error instanceof Error ? error.message : 'upload failed'}.`);
        }
      } else {
        // An existing reference brings its stored photo along; the same document serves every
        // farmer that referrer brought in.
        const wanted = referrerByName ? form.referred_by_photo_document_id || null : null;
        if (wanted !== (editing?.referred_by_photo_document_id ?? null)) {
          await callRpc('fn_set_referrer_photo', { p_farmer_id: farmerId, p_document_id: wanted });
        }
      }
      if (houseChanged && house) {
        try {
          await callRpc('fn_set_farmer_house_location', {
            p_farmer_id: farmerId, p_lat: house.lat, p_lng: house.lng, p_accuracy_m: house.accuracy,
          });
          toast.success(`House location saved at ${house.lat.toFixed(6)}, ${house.lng.toFixed(6)}.`);
        } catch (error) {
          toast.error(`Farmer saved, but the house location was not: ${error instanceof Error ? error.message : 'save failed'}.`);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['farmers'] });
      if (asDraft) toast.success('Saved as a draft. Complete it when you have the rest.');
      setEditing(null);
      setCreating(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  const open = creating || Boolean(editing);

  return (
    <LiveScreen
      actions={<AddButton disabled={!mayCreate} label="New farmer" onClick={openCreate}
                          title={mayCreate ? undefined : 'Your role cannot create farmers.'} />}
      subtitle="Identity, contact channels, KYC and plots for every grower in your geography."
      title="Farmer register"
    >
      <div className="toolbar-row">
        <span className="spacer" />
        {scope !== 'all' && (
          <span className="muted">
            <Users size={13} /> Showing {scope === 'own' ? 'only farmers you registered' : "your team's farmers"}
          </span>
        )}
      </div>

      <StatCards active={stat} onSelect={setStat} rows={farmers.data ?? []} secondary={{ sum: (row) => row.total_mapped_area_ha, unit: 'ha' }} stats={STATS} />

      <QueryState empty="No farmers match. Register the first one." query={farmers}>
        <DataGrid
          columns={columns}
          exportName="farmers"
          onRowClick={setDetail}
          onSearchChange={setSearch}
          search={search}
          searchPlaceholder="Name, code or mobile"
          rows={visible}
          screenKey="farmer.register"
          selectedId={detail?.id}
        />
      </QueryState>

      <RecordDrawer
        record={editing ? { table: 'people.farmers', id: editing.id, historyIds: [editing.party_id], invalidate: ['farmers'] } : null}
        footer={
          <>
            <button className="cancel" onClick={() => { setCreating(false); setEditing(null); }} type="button">Cancel</button>
            {(!editing || editing.is_draft) && (
              <button disabled={saving} onClick={() => void handleSave(true)}
                      title="Save what you have; only the name and village are needed" type="button">
                <FileText size={15} /> Save draft
              </button>
            )}
            <button className="primary" disabled={saving || outstanding.length > 0} onClick={() => void handleSave(false)}
                    title={outstanding.length > 0 ? 'Fill in what is listed above this button first' : undefined} type="button">
              {saving ? 'Saving...' : editing?.is_draft ? 'Complete & save' : editing ? 'Save changes' : 'Register farmer'}
            </button>
            {/* Last, so the cluster's wrap-reverse lifts it onto its own line above the buttons. */}
            {outstanding.length > 0 && (
              <div className="drawer-actions-note"><span>Still needed: {outstanding.map((key) => NEEDED_LABEL[key] ?? key).join(', ')}</span></div>
            )}
          </>
        }
        onClose={() => { setCreating(false); setEditing(null); }}
        sections={{
          items: FORM_TABS.map((entry) => {
            const count = Object.entries(errors).filter(([key, value]) => value && TAB_OF[key] === entry.id).length;
            return { id: entry.id, label: <>{entry.label}{count > 0 && <span className="tab-error-count">{count}</span>}</> };
          }),
          active: formTab,
          onChange: (id) => setFormTab(id as FormTab),
        }}
        open={open}
        subtitle={editing ? editing.farmer_code : 'The code is issued when the record is saved.'}
        title={editing
          ? (form.member_type === 'entity' ? 'Edit entity' : 'Edit farmer')
          : (form.member_type === 'entity' ? 'Register an entity' : 'Register a farmer')}
      >

        {formTab === 'basics' && (<>
        <div aria-label="Member type" className="view-toggle member-type" role="group">
          <button aria-pressed={form.member_type === 'farmer'} onClick={() => setForm({ ...form, member_type: 'farmer' })} type="button">
            Farmer
          </button>
          <button aria-pressed={form.member_type === 'entity'} onClick={() => setForm({ ...form, member_type: 'entity' })} type="button">
            Entity (FPO, cooperative, company ...)
          </button>
        </div>
        <div className="form-photo">
          <FarmerPhoto name={form.full_name} size={84}
                       url={photoPreview ?? (editing?.photo_document_id ? existingPhoto.data?.[editing.photo_document_id] : null)} />
          <div className="form-photo-actions">
            <DropZone accept="image/*" buttonLabel={photoPreview || editing?.photo_document_id ? 'Change photo' : 'Add photo'}
                      label="Drag a photo here, or" onFile={pickPhoto} />
            {photoPreview && (
              <button onClick={() => { setPhotoFile(null); setPhotoPreview((old) => { if (old) URL.revokeObjectURL(old); return null; }); }}
                      type="button"><X size={14} /> Remove</button>
            )}
            <small className="muted">A clear face photo, taken now or chosen from the phone. Stored privately.</small>
          </div>
        </div>
        {form.member_type === 'entity' && (
          <div className="field-row">
            <Field error={errors.entity_type} label="Entity type" required>
              <Select onChange={(value) => setForm({ ...form, entity_type: value })} options={ENTITY_TYPES}
                      placeholder="Select type" value={form.entity_type} />
            </Field>
            <Field hint="Registration / incorporation number" label="Registration no.">
              <input onChange={(event) => setForm({ ...form, registration_no: event.target.value })} value={form.registration_no} />
            </Field>
          </div>
        )}
        <Field error={errors.full_name} label={form.member_type === 'entity' ? 'Entity name' : 'Full name'} required>
          <input onChange={(event) => setForm({ ...form, full_name: event.target.value })} value={form.full_name} />
        </Field>
        {form.member_type === 'entity' ? (
          <Field hint="Who speaks for the entity - usually its secretary, president or director." label="Contact person">
            <input onChange={(event) => setForm({ ...form, contact_person_name: event.target.value })} value={form.contact_person_name} />
          </Field>
        ) : (
          <>
            <Field label="Gender">
              <Select onChange={(value) => setForm({ ...form, gender: value })} options={GENDERS} value={form.gender} />
            </Field>
            {/* The relation as the identity document prints it (owner, 2026-09-28: "son of, etc."). */}
            <div className="field-row">
              <Field hint="As printed on the ID." label="Relation">
                <Select onChange={(value) => setForm({ ...form, relation_type: value })} options={RELATION_TYPES} value={form.relation_type} />
              </Field>
              <Field label="Father's or spouse's name" hint="As written on the identity document.">
                <input onChange={(event) => setForm({ ...form, father_or_spouse_name: event.target.value })}
                       value={form.father_or_spouse_name} />
              </Field>
            </div>
            <div className="field-row">
              <Field hint="Optional. Seen only by roles allowed to reveal personal details." label="Community">
                <Select onChange={(value) => setForm({ ...form, social_category: value })} options={COMMUNITIES} value={form.social_category} />
              </Field>
              <Field hint="Optional. Not on the list? Pick the last option to add it." label="Caste">
                <CastePicker onChange={(value) => setForm({ ...form, caste: value })} value={form.caste} />
              </Field>
            </div>
            <div className="field-row">
              <Field hint="Indigenous people / Scheduled Tribe (FSC Principle 3). ST is taken as Yes unless changed." label="Indigenous people (ST)?">
                <div className="view-toggle member-type" role="group" aria-label="Indigenous">
                  {([['yes', 'Yes'], ['no', 'No'], ['', 'Not recorded']] as const).map(([value, label]) => (
                    <button aria-pressed={(form.is_indigenous || (form.social_category === 'st' && form.is_indigenous === '' ? 'yes' : '')) === value}
                            key={label} onClick={() => setForm({ ...form, is_indigenous: value })} type="button">{label}</button>
                  ))}
                </div>
              </Field>
              {(form.is_indigenous === 'yes' || (form.is_indigenous === '' && form.social_category === 'st')) && (
                <Field label="Indigenous community / tribe">
                  <input onChange={(event) => setForm({ ...form, indigenous_community: event.target.value, is_indigenous: 'yes' })}
                         placeholder="e.g. Santhal, Oraon, Munda" value={form.indigenous_community} />
                </Field>
              )}
            </div>
          </>
        )}

        </>)}

        {formTab === 'reference' && (<>
        <div className="section-heading"><h3>Referred by</h3></div>
        {/* Who first, then which one (owner, 2026-09-27: "have to reverse?"): the referrers already
            on record of that kind come first, and adding someone new is the last choice. */}
        <Field label="Who referred this member">
          <Select onChange={(value) => { setForm({ ...form, ...NO_REFERRER, referred_by_type: value }); clearReferrerPhoto(); }}
                  options={REFERRERS.filter((option) => option.id)} placeholder="Not referred" value={form.referred_by_type} />
        </Field>
        {form.referred_by_type && (() => {
          const farmerKind = isFarmerKind(form.referred_by_type);
          const onRecord = (references.data ?? []).filter((row) => row.referred_by_type === form.referred_by_type
            && (!farmerKind || !row.referred_by_farmer_id));
          const kind = (REFERRER_LABEL[form.referred_by_type] ?? 'referrer').toLowerCase();
          return (
            <Field error={errors.referrer_pick} hint={farmerKind ? 'Farmers on the register, then any recorded by name.' : `${REFERRER_LABEL[form.referred_by_type]} already on record, or add someone new.`}
                   label="Referrer" required>
              <Select onChange={(value) => {
                        const existing = onRecord.find((row) => row.referrer_key === value);
                        clearReferrerPhoto();
                        if (!value) { setForm({ ...form, ...NO_REFERRER, referred_by_type: form.referred_by_type }); return; }
                        if (value === NEW_REFERRER) {
                          setForm({ ...form, ...NO_REFERRER, referred_by_type: form.referred_by_type, referrer_pick: value, referrer_unlisted: farmerKind });
                        } else if (existing) {
                          setForm({
                            ...form, referrer_pick: value, referred_by_farmer_id: '', referrer_unlisted: farmerKind,
                            referred_by_name: existing.referred_by_name ?? '', referred_by_phone: existing.referred_by_phone ?? '',
                            referred_by_photo_document_id: existing.referrer_photo_document_id ?? '',
                          });
                        } else {
                          setForm({ ...form, ...NO_REFERRER, referred_by_type: form.referred_by_type, referrer_pick: value, referred_by_farmer_id: value });
                        }
                      }}
                      options={[
                        ...(farmerKind ? (referrers.data ?? []).filter((option) => option.id !== editing?.id) : []),
                        ...onRecord.map((row) => ({
                          id: row.referrer_key,
                          label: [row.referred_by_name ?? '--', row.referred_by_phone, farmerKind ? 'not on the register' : null].filter(Boolean).join(' · '),
                        })),
                        { id: NEW_REFERRER, label: farmerKind ? '+ Add a farmer not on the register' : `+ Add a new ${kind}` },
                      ]}
                      placeholder={references.isLoading || (farmerKind && referrers.isLoading) ? 'Loading...' : 'Select referrer'}
                      value={form.referrer_pick} />
            </Field>
          );
        })()}

        {form.referrer_pick && form.referrer_pick !== NEW_REFERRER && (() => {
          const existing = (references.data ?? []).find((row) => row.referrer_key === form.referrer_pick);
          if (!existing) return null;
          return (
            <div className="form-photo">
              <FarmerPhoto name={existing.referred_by_name} size={64}
                           url={existing.referrer_photo_document_id ? referencePhotos.data?.[existing.referrer_photo_document_id] : null} />
              <div className="form-photo-actions">
                <strong>{existing.referred_by_name ?? '--'}</strong>
                <span className="muted">
                  {[REFERRER_LABEL[existing.referred_by_type] ?? existing.referred_by_type, existing.referred_by_phone,
                    `referred ${existing.farmers_referred} member${existing.farmers_referred === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                </span>
              </div>
            </div>
          );
        })()}

        {form.referrer_pick === NEW_REFERRER && (<>
          <div className="field-row">
            <Field error={errors.referred_by_name} label="Referrer's name" required>
              <input onChange={(event) => setForm({ ...form, referred_by_name: event.target.value })} value={form.referred_by_name} />
            </Field>
            <Field error={errors.referred_by_phone} label="Referrer's phone">
              <input inputMode="tel" onChange={(event) => setForm({ ...form, referred_by_phone: event.target.value })}
                     placeholder="98xxxxxx01" value={form.referred_by_phone} />
            </Field>
          </div>
          <div className="form-photo">
            <FarmerPhoto name={form.referred_by_name || 'Referrer'} size={64} url={referrerPhotoPreview} />
            <div className="form-photo-actions">
              <DropZone accept="image/*" buttonLabel={referrerPhotoPreview ? 'Change referrer photo' : 'Add referrer photo'}
                        label="Drag a photo here, or" onFile={(file) => pickPhoto(file, true)} />
              {referrerPhotoPreview && (
                <button onClick={clearReferrerPhoto} type="button"><X size={14} /> Remove</button>
              )}
              <small className="muted">Optional. Tells apart two referrers with the same name. Stored privately.</small>
            </div>
          </div>
        </>)}
        </>)}

        {formTab === 'kyc' && (<>
        <div className="section-heading"><h3>{form.member_type === 'entity' ? 'Registration' : 'Identity'}</h3></div>
        <Field hint={form.member_type === 'entity' ? 'Exactly as on the registration certificate.' : 'Exactly as printed on the ID card (Aadhaar, voter ID ...) - spelling, initials and all.'}
               label={form.member_type === 'entity' ? 'Name as per registration' : 'Name as per ID card'}>
          <input onChange={(event) => setForm({ ...form, name_as_per_id: event.target.value })} placeholder={form.full_name} value={form.name_as_per_id} />
        </Field>
        {idNameDiffers && (
          <div className="name-match">
            <p className="muted" style={{ margin: 0 }}>The name as per ID card differs from the name on the Member tab. Record what confirms it is the same person; it is kept on the farmer's record. (Names on KYC and land documents are checked by someone else.)</p>
            <Field error={errors.name_match_basis} label="Confirmed by">
              <NameMatchBasisSelect onChange={(value) => setForm((now) => ({ ...now, name_match_basis: value }))} recorded={form.full_name} reference={form.name_as_per_id} value={form.name_match_basis} />
            </Field>
            <Field error={errors.name_match_remarks} label="Remarks">
              <input onChange={(event) => setForm({ ...form, name_match_remarks: event.target.value })}
                     placeholder="e.g. Voter ID and ration card both read 'Md. Rahim'" value={form.name_match_remarks} />
            </Field>
          </div>
        )}
        <div className="section-heading"><h3>KYC documents</h3></div>
        {editing?.party_id
          ? <FarmerKycSection inline partyId={editing.party_id} partyLabel={editing.full_name} />
          : (<>
            {/* The full KYC form here too; the records are held and saved with the farmer (owner,
                2026-09-27: "full kyc in farmers registration tab?"). The farmer stays a party:
                these records belong to the party the form creates. */}
            <KycRecordDrawer inline onClose={() => undefined} onQueue={(item) => setPendingKyc((now) => [...now, item])} open
                             referenceName={form.name_as_per_id.trim() || form.full_name.trim() || null} />
            {pendingKyc.length > 0 && (
              <ul className="land-document-list">
                {pendingKyc.map((item) => (
                  <li key={item.key}>
                    <span className="land-document-text">
                      <strong>{item.typeLabel}</strong>
                      <small className="muted">{[`ending ${item.values.document_number.trim().slice(-4)}`, item.values.valid_to ? `valid to ${formatDate(item.values.valid_to)}` : null,
                        item.file ? item.file.name : 'no file', 'saved with the farmer'].filter(Boolean).join(' · ')}</small>
                    </span>
                    <button onClick={() => setPendingKyc((now) => now.filter((other) => other.key !== item.key))} type="button"><X size={14} /> Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </>)}
        </>)}

        {formTab === 'address' && (<>
        <GeographyPicker
          error={errors.village_id}
          onChange={(villageId) => setForm({ ...form, village_id: villageId })}
          value={form.village_id}
        />

        <Field hint="Where letters and notices reach the farmer, if not the village above."
               label="Communication address">
          <textarea onChange={(event) => setForm({ ...form, communication_address: event.target.value })}
                    rows={3} value={form.communication_address} />
        </Field>
        <Field error={errors.pin_code} label="PIN code">
          <input inputMode="numeric" maxLength={6}
                 onChange={(event) => setForm({ ...form, pin_code: event.target.value.replace(/\D/g, '') })}
                 value={form.pin_code} />
        </Field>

        <div className="section-heading"><h3>House location</h3></div>
        <HouseLocationPicker onChange={(point) => { setHouse(point); setHouseChanged(true); }}
                             placeHint={villagePlace.data} value={house} />
        </>)}

        {formTab === 'contact' && (<>
        <div className="section-heading"><h3>Contact channels</h3></div>
        <div aria-label="How this farmer is reached" className="view-toggle" role="group">
          {(['digital', 'offline'] as const).map((mode) => (
            <button aria-pressed={form.contact_mode === mode} key={mode}
                    onClick={() => setForm({ ...form, contact_mode: mode })} type="button">
              {mode === 'digital' ? 'Digital' : 'Offline'}
            </button>
          ))}
        </div>
        {form.contact_mode === 'offline' ? (
          <p className="muted" style={{ margin: 0 }}>
            Reached in person by field staff. No message, code or call goes out automatically, and
            consent is collected on paper in the village.
          </p>
        ) : (<>
        <p className="muted" style={{ margin: 0 }}>
          Switch on the ways this farmer can be reached and put them in the order to try. A message
          goes out on the first one that works; a channel left off is never used. WhatsApp is often a
          different number from the one that receives SMS, and a one-time code only ever goes to the
          number recorded here.
        </p>
        <RankedList
          items={CHANNEL_IDS.map((channel) => {
            const key = CHANNEL_FIELD[channel];
            const filled = Boolean(form[key].trim());
            return {
              id: channel,
              label: CHANNEL_LABEL[channel],
              blockedReason: filled ? undefined : `Enter ${CHANNEL_NEEDS[channel].split(' - ')[0]} first.`,
              field: channel === 'call' ? (
                <span className="muted">{filled ? `Rings ${form.mobile.trim()}` : 'Rings the SMS mobile number'} - placed by a person</span>
              ) : (
                <div className="live-field">
                  <input aria-invalid={errors[key] ? true : undefined} aria-label={channel === 'sms' ? 'Mobile' : CHANNEL_LABEL[channel]}
                         inputMode={channel === 'email' ? 'email' : 'tel'}
                         onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                         placeholder={channel === 'email' ? 'name@example.com' : '98xxxxxx01'} value={form[key]} />
                  {errors[key] && <small className="field-error">{errors[key]}</small>}
                </div>
              ),
            };
          })}
          label="Contact channels, in order"
          onChange={(channel_order) => setForm({ ...form, channel_order })}
          value={form.channel_order}
        />
        {errors.channel_order && <div className="live-field"><small className="field-error">{errors.channel_order}</small></div>}
        </>)}

        <div className="section-heading"><h3>Languages</h3></div>
        <p className="muted" style={{ margin: 0 }}>
          English is always first and the state's language second: every consent is made in both, and
          messages go out in the state's language where translated. A third language is optional.
        </p>
        <RankedList
          items={languageOptions.map((option) => ({ id: option.id, label: option.label }))}
          label="Languages, in order"
          pinned={pinnedLanguages}
          onChange={(language_order) => setForm({ ...form, language_order })}
          value={form.language_order}
        />
        {errors.language_order && <div className="live-field"><small className="field-error">{errors.language_order}</small></div>}
        </>)}
      </RecordDrawer>

      {detail && (
        <FarmerDetail
          canEdit={mayEdit}
          farmer={(farmers.data ?? []).find((row) => row.id === detail.id) ?? detail}
          onClose={() => setDetail(null)}
          onEdit={() => { setDetail(null); openEdit(detail); }}
        />
      )}
    </LiveScreen>
  );
}
